import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { calcProposal, inr } from '../mock';
import { proposalsApi, catalogApi } from '../lib/api';
import RooftopTracer from '../components/RooftopTracer';
import { Check, ChevronLeft, ChevronRight, User, Home, Sun, Calculator, FileCheck, Zap, IndianRupee, Leaf, TrendingUp, Sparkles, Loader2, Map, BadgeCheck, Layers, Battery, Cable, Wrench, Shield } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const STEPS = [
  { id: 1, label: 'System Type', icon: Layers },
  { id: 2, label: 'Lead Info', icon: User },
  { id: 3, label: 'Site & System', icon: Home },
  { id: 4, label: 'Components', icon: Sun },
  { id: 5, label: 'Financials', icon: Calculator },
  { id: 6, label: 'Generate', icon: FileCheck },
];

const SYSTEM_TYPES = [
  { key: 'on-grid',  title: 'On-Grid',  desc: 'Grid-tied. Sell surplus back via net-metering. Ideal for daily savings.', hint: 'PM Surya Ghar subsidy eligible' },
  { key: 'off-grid', title: 'Off-Grid', desc: 'Standalone with lead-acid batteries. For unreliable-grid or remote sites.', hint: 'Luminous inverter + Exide batteries' },
  { key: 'hybrid',   title: 'Hybrid',   desc: 'Grid-tied + Lithium backup. Best of both — savings & backup power.', hint: 'Deye / Growatt / Sungrow · Lithium storage' },
];

export default function NewProposal() {
  const nav = useNavigate();
  const loc = useLocation();
  const seedLead = loc.state?.lead;
  const seedDesign = useMemo(() => {
    try {
      const raw = localStorage.getItem('inpace_studio_design');
      if (!raw) return null;
      const d = JSON.parse(raw);
      localStorage.removeItem('inpace_studio_design');
      return d;
    } catch { return null; }
  }, []);

  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    system_type: 'on-grid',
    phase: 'single',       // single | three (for inverter filter)
    name: seedLead?.name || '',
    phone: seedLead?.phone || '',
    email: '',
    address: seedLead?.city || 'Bangalore',
    segment: seedLead?.segment || 'Residential',
    monthlyBill: 5000,
    roofArea: 600,
    roofType: 'RCC',
    structureType: 'low-rise',
    structureQuantity: 1,
    withSubsidy: true,   // toggle: apply PM Surya Ghar subsidy or generate a non-subsidy proposal
    discountPct: 0,      // sales team discount % (0, 3, 4 or 5)
    shading: seedDesign?.shading ?? 5,
    tilt: seedDesign?.tilt ?? 15,
    azimuth: 180,
    kw: seedDesign?.kw || seedLead?.kw || 5,
    panelId: '',
    panelQty: 0,           // 0 => auto from kW
    inverterId: '',
    batteryId: '',
    batteryQty: 1,
    cableId: '',           // cable line item id
    floors: 1,
    structureId: '',       // selected mounting structure item id (from catalog)
    accessoryIds: [],      // list of selected accessory ids
    accessoryQty: {},      // { [accessoryId]: qty }
    notes: seedDesign ? 'Design imported from Design Studio' : '',
    polygon: null,
  });

  // Catalog (full — filtered per step)
  const [allPanels, setAllPanels] = useState([]);
  const [allInverters, setAllInverters] = useState([]);
  const [allBatteries, setAllBatteries] = useState([]);
  const [allCables, setAllCables] = useState([]);
  const [allStructures, setAllStructures] = useState([]);
  const [allAccessories, setAllAccessories] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [mnreOnly, setMnreOnly] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, i, b, c, s, a] = await Promise.all([
          catalogApi.list('panel'),
          catalogApi.list('inverter'),
          catalogApi.list('battery'),
          catalogApi.list('cable'),
          catalogApi.list('structure'),
          catalogApi.list('accessory'),
        ]);
        setAllPanels(p); setAllInverters(i); setAllBatteries(b); setAllCables(c);
        setAllStructures(s); setAllAccessories(a);
      } catch (e) {
        toast.error('Could not load component catalog');
      } finally { setCatalogLoading(false); }
    })();
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Auto-derive phase from kW (<=6 => single, else three) unless user forces
  useEffect(() => {
    if (Number(form.kw) > 6 && form.phase === 'single') set('phase', 'three');
    if (Number(form.kw) <= 6 && form.phase === 'three') set('phase', 'single');
  }, [form.kw]);

  // ---- Filtered catalog by system_type + phase + MNRE
  const panels = useMemo(() => {
    let ps = allPanels.filter(p => !p.system_type || p.system_type === 'universal' || p.system_type === form.system_type);
    if (mnreOnly) ps = ps.filter(p => p.mnre_approved);
    return ps;
  }, [allPanels, form.system_type, mnreOnly]);

  const inverters = useMemo(() => {
    // Filter by system_type: for hybrid → hybrid, for off-grid → off-grid, else on-grid
    let ivs = allInverters.filter(i => (i.system_type || 'on-grid') === form.system_type);
    if (form.phase) ivs = ivs.filter(i => !i.phase || i.phase === form.phase);
    if (mnreOnly) ivs = ivs.filter(i => i.mnre_approved);
    return ivs;
  }, [allInverters, form.system_type, form.phase, mnreOnly]);

  const batteries = useMemo(() => {
    // Batteries required for off-grid & hybrid, filtered by system_type
    return allBatteries.filter(b => b.system_type === form.system_type);
  }, [allBatteries, form.system_type]);

  const cables = useMemo(() => {
    return allCables.filter(c => !c.system_type || c.system_type === 'universal');
  }, [allCables]);

  const structures = useMemo(() => {
    // Show all structure items — no filter by type; sales rep picks any of the 3 available.
    return allStructures;
  }, [allStructures]);

  const accessories = allAccessories;

  // Auto-pick first available items when filters change
  useEffect(() => {
    setForm(f => {
      const upd = { ...f };
      if (!panels.find(p => p.id === f.panelId)) upd.panelId = panels[0]?.id || '';
      if (!inverters.find(i => i.id === f.inverterId)) upd.inverterId = inverters[0]?.id || '';
      if (f.system_type === 'on-grid') {
        upd.batteryId = '';
      } else if (!batteries.find(b => b.id === f.batteryId)) {
        upd.batteryId = batteries[0]?.id || '';
      }
      if (!cables.find(c => c.id === f.cableId)) {
        // Prefer cable that matches selected floors
        const match = cables.find(c => (c.model || '').includes(`${f.floors} floor`));
        upd.cableId = match?.id || cables[0]?.id || '';
      }
      if (!structures.find(s => s.id === f.structureId)) {
        upd.structureId = structures[0]?.id || '';
      }
      return upd;
    });
  }, [panels, inverters, batteries, cables, structures]);

  // When floors change, auto-select the matching cable price
  useEffect(() => {
    const match = cables.find(c => (c.model || '').includes(`${form.floors} floor`));
    if (match && match.id !== form.cableId) set('cableId', match.id);
  }, [form.floors, cables]);

  const panel = panels.find(p => p.id === form.panelId);
  const inverter = inverters.find(i => i.id === form.inverterId);
  const battery = batteries.find(b => b.id === form.batteryId);
  const cable = cables.find(c => c.id === form.cableId);
  const structure = structures.find(s => s.id === form.structureId);
  const selectedAccessories = useMemo(
    () => accessories.filter(a => form.accessoryIds.includes(a.id)),
    [accessories, form.accessoryIds]
  );

  const autoPanelCount = Math.ceil((form.kw * 1000) / (panel?.wattage || 540));
  const numPanels = form.panelQty > 0 ? form.panelQty : autoPanelCount;

  // Financials — base from mock + extras (battery + cable + structure + accessories)
  const baseResult = useMemo(
    () => calcProposal({ kw: Number(form.kw), segment: form.segment }),
    [form.kw, form.segment]
  );
  const batteryCost = battery ? Number(battery.price) * Math.max(1, form.batteryQty) : 0;
  const cableCost = cable ? Number(cable.price) : 0;
  const structureCost = structure ? Number(structure.price) * Math.max(1, Number(form.structureQuantity) || 1) : 0;
  const accessoriesCost = useMemo(() => selectedAccessories.reduce(
    (sum, a) => sum + Number(a.price) * Math.max(1, Number(form.accessoryQty[a.id]) || 1),
    0
  ), [selectedAccessories, form.accessoryQty]);
  const result = useMemo(() => {
    // ACTUAL BOM sum from selected line items (matches PDF exactly, no formula gross)
    const invKw = Number(inverter?.kw || 5);
    const inverterQty = invKw > 0 ? Math.max(1, Math.ceil(Number(form.kw) / invKw)) : 1;
    const panelsCost = Number(panel?.price || 0) * Number(numPanels || 0);
    const inverterCostReal = Number(inverter?.price || 0) * inverterQty;
    const cableCostReal = cable ? Number(cable.price) : Math.round(Number(form.kw) * 1500);
    const structureCostReal = structure
      ? Number(structure.price) * Math.max(1, Number(form.structureQuantity) || 1)
      : Math.round(Number(form.kw) * 4500);
    const netMeterPrice = 12000;
    const installationPrice = Math.round(Number(form.kw) * 3000);
    const bomTotal = Math.round(
      panelsCost + inverterCostReal + batteryCost + cableCostReal + structureCostReal +
      netMeterPrice + installationPrice + accessoriesCost
    );

    // Discount + GST + Subsidy → Net Payable
    // Order: BOM → +GST → After GST → -Discount → After Discount → -Subsidy → Net Payable
    const GST_PCT = 8.9;
    const discPct = Math.max(0, Math.min(5, Number(form.discountPct) || 0));
    const discountAmount = Math.round(bomTotal * discPct / 100);
    const gstAmount = Math.round(bomTotal * GST_PCT / 100);
    const afterGst = bomTotal + gstAmount;
    const afterDiscount = afterGst - discountAmount;
    const taxableValue = Math.max(0, bomTotal - discountAmount);
    const subsidy = form.withSubsidy ? baseResult.subsidy : 0;
    const totalWithGst = afterDiscount - subsidy;              // Net Payable by Customer
    const netCost = Math.max(0, taxableValue - subsidy);       // legacy
    const payback = baseResult.yearlySavings ? Math.round((totalWithGst / baseResult.yearlySavings) * 10) / 10 : 0;
    return {
      ...baseResult,
      grossCost: bomTotal,
      bomTotal, subsidy, netCost, payback,
      discountPct: discPct, discountAmount,
      gstPct: GST_PCT, taxableValue, gstAmount, afterGst, afterDiscount, totalWithGst,
    };
  }, [baseResult, panel, inverter, cable, structure, batteryCost, accessoriesCost, form.kw, form.structureQuantity, form.withSubsidy, form.discountPct, numPanels]);

  // (gridLayout removed — no longer used since 3D preview was removed)

  const next = () => {
    if (step === 2 && (!form.name || !form.phone)) { toast.error('Customer name and phone are required'); return; }
    if (step === 4) {
      if (!panel || !inverter) { toast.error('Pick a panel and inverter'); return; }
      if (form.system_type !== 'on-grid' && !battery) { toast.error(`Please pick a battery for ${form.system_type} system`); return; }
    }
    setStep(s => Math.min(6, s + 1));
  };
  const prev = () => setStep(s => Math.max(1, s - 1));

  const [saving, setSaving] = useState(false);
  const finalize = async () => {
    if (!panel || !inverter) { toast.error('Pick a panel and inverter'); return; }
    setSaving(true);
    try {
      const design3d = {
        polygon: form.polygon || null,
        tilt: Number(form.tilt),
        azimuth: Number(form.azimuth) || 180,
        panel_w: 1.13,
        panel_h: 2.28,
      };
      const payload = {
        lead_id: seedLead?.id,
        customer: form.name,
        phone: form.phone,
        email: form.email,
        address: form.address,
        segment: form.segment,
        system_type: form.system_type,
        kw: Number(form.kw),
        roofArea: Number(form.roofArea),
        roofType: form.roofType,
        structureType: form.structureType,
        shading: Number(form.shading),
        tilt: Number(form.tilt),
        panel: {
          id: panel.id, brand: panel.brand, model: panel.model,
          wattage: panel.wattage, price: panel.price,
        },
        inverter: {
          id: inverter.id, brand: inverter.brand, model: inverter.model,
          kw: inverter.kw, price: inverter.price, phase: inverter.phase || form.phase,
        },
        numPanels,
        battery: battery ? {
          id: battery.id, brand: battery.brand, model: battery.model,
          price: battery.price, qty: form.batteryQty,
          kva: battery.kva || null, ah: battery.ah || null,
        } : null,
        cable: cable ? {
          id: cable.id, floors: form.floors, price: cable.price,
          description: cable.description || null,
        } : null,
        structure: structure ? {
          id: structure.id, brand: structure.brand, model: structure.model,
          price: structure.price, qty: Number(form.structureQuantity) || 1,
          structure_type: form.structureType,
        } : null,
        structureQuantity: Number(form.structureQuantity) || 1,
        accessories: selectedAccessories.map(a => ({
          id: a.id, brand: a.brand, model: a.model,
          price: Number(a.price),
          qty: Math.max(1, Number(form.accessoryQty[a.id]) || 1),
        })),
        monthlyBill: Number(form.monthlyBill),
        notes: form.notes,
        withSubsidy: form.withSubsidy,
        discountPct: Number(form.discountPct) || 0,
        design3d,
      };
      const created = await proposalsApi.create(payload);
      toast.success(`Proposal ${created.id} generated!`);
      nav('/app/proposals/' + created.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create proposal');
    } finally {
      setSaving(false);
    }
  };

  const stTitle = SYSTEM_TYPES.find(s => s.key === form.system_type)?.title || 'On-Grid';

  return (
    <div className="max-w-6xl mx-auto" data-testid="new-proposal-page">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-emerald-950">New Solar Proposal</h1>
          <p className="text-slate-600 mt-1">Complete {STEPS.length} quick steps to generate a customer-ready proposal.</p>
        </div>
        <Button variant="outline" onClick={() => nav('/app/proposals')} data-testid="cancel-btn">Cancel</Button>
      </div>

      {/* Stepper */}
      <div className="mb-8">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const done = s.id < step;
            const active = s.id === step;
            return (
              <React.Fragment key={s.id}>
                <div className="flex items-center gap-3 shrink-0">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center border-2 transition-colors ${
                    done ? 'bg-emerald-600 border-emerald-600 text-white' :
                    active ? 'bg-amber-500 border-amber-500 text-emerald-950' :
                    'bg-white border-emerald-100 text-slate-400'
                  }`}>
                    {done ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <div className="hidden md:block">
                    <div className="text-xs text-slate-500">Step {s.id}</div>
                    <div className={`text-sm font-semibold ${active ? 'text-emerald-900' : 'text-slate-600'}`}>{s.label}</div>
                  </div>
                </div>
                {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 min-w-[20px] rounded ${done ? 'bg-emerald-600' : 'bg-emerald-100'}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-7 border-emerald-100">
          {/* STEP 1: System Type */}
          {step === 1 && (
            <div className="space-y-5" data-testid="step-system-type">
              <div>
                <h2 className="text-xl font-bold text-emerald-950">Choose System Type</h2>
                <p className="text-sm text-slate-500 mt-1">This determines available inverters, batteries, and BOM lines.</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                {SYSTEM_TYPES.map(t => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => set('system_type', t.key)}
                    data-testid={`system-type-${t.key}`}
                    className={`text-left p-5 rounded-xl border-2 transition-all ${
                      form.system_type === t.key
                        ? 'border-emerald-600 bg-emerald-50 shadow-sm'
                        : 'border-emerald-100 hover:border-emerald-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Layers className={`w-5 h-5 ${form.system_type === t.key ? 'text-emerald-700' : 'text-slate-400'}`} />
                      <span className="text-lg font-extrabold text-emerald-950">{t.title}</span>
                    </div>
                    <div className="text-xs text-slate-600 mt-2 leading-relaxed">{t.desc}</div>
                    <div className="mt-3 inline-flex text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                      {t.hint}
                    </div>
                  </button>
                ))}
              </div>
              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-sm flex gap-3">
                <Sparkles className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div className="text-emerald-900">
                  Selected: <strong>{stTitle}</strong> — {form.system_type === 'off-grid'
                    ? 'Off-grid uses Luminous inverter + Exide lead-acid battery sets (1–20).'
                    : form.system_type === 'hybrid'
                    ? 'Hybrid uses Deye / Growatt / Sungrow inverters with Lithium battery storage.'
                    : 'On-grid uses standard grid-tied inverters (Deye, Waaree, Solis, Growatt, Sungrow).'}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Lead Info */}
          {step === 2 && (
            <div className="space-y-5" data-testid="step-lead-info">
              <h2 className="text-xl font-bold text-emerald-950">Customer Details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="mb-1.5 block">Full Name *</Label><Input data-testid="customer-name" value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Rohit Sharma" /></div>
                <div><Label className="mb-1.5 block">Phone *</Label><Input data-testid="customer-phone" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+91 …" /></div>
                <div><Label className="mb-1.5 block">Email</Label><Input data-testid="customer-email" value={form.email} onChange={e => set('email', e.target.value)} type="email" placeholder="customer@email.com" /></div>
                <div><Label className="mb-1.5 block">Address / City</Label><Input value={form.address} onChange={e => set('address', e.target.value)} /></div>
                <div>
                  <Label className="mb-1.5 block">Segment</Label>
                  <Select value={form.segment} onValueChange={v => set('segment', v)}>
                    <SelectTrigger data-testid="segment-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Residential">Residential</SelectItem>
                      <SelectItem value="Commercial">Commercial</SelectItem>
                      <SelectItem value="Industrial">Industrial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="mb-1.5 block">Avg Monthly Bill (₹)</Label>
                  <Input type="number" value={form.monthlyBill} onChange={e => set('monthlyBill', Number(e.target.value))} />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Site & System */}
          {step === 3 && (
            <div className="space-y-5" data-testid="step-site-system">
              <h2 className="text-xl font-bold text-emerald-950">Site & System Sizing</h2>

              <div className="rounded-xl border border-emerald-100 p-4 bg-gradient-to-br from-emerald-50/50 to-white">
                <div className="flex items-center gap-2 mb-3">
                  <Map className="w-4 h-4 text-emerald-700" />
                  <h3 className="text-sm font-bold text-emerald-900">Trace the customer&apos;s rooftop on satellite</h3>
                </div>
                <RooftopTracer
                  initialAddress={form.address}
                  onAreaChange={(sqft) => {
                    if (sqft > 0) {
                      set('roofArea', sqft);
                      const cap = Math.max(1, Math.floor(sqft / 80));
                      set('kw', Math.min(500, cap));
                    }
                  }}
                  onPolygonChange={(poly) => set('polygon', poly)}
                  onSegmentDetected={({ tilt, azimuth }) => {
                    // Pre-fill tilt from Solar API (clamped to slider range 0-45)
                    if (typeof tilt === 'number' && tilt >= 0) {
                      set('tilt', Math.min(45, Math.max(0, tilt)));
                    }
                    // Save azimuth for downstream 3D rendering
                    if (typeof azimuth === 'number') set('azimuth', azimuth);
                  }}
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block">Roof Type</Label>
                  <Select value={form.roofType} onValueChange={v => set('roofType', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RCC">RCC (Concrete)</SelectItem>
                      <SelectItem value="Metal Sheet">Metal Sheet</SelectItem>
                      <SelectItem value="Asbestos">Asbestos</SelectItem>
                      <SelectItem value="Ground Mount">Ground Mount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-1.5 block">Roof Area (sq.ft)</Label><Input type="number" value={form.roofArea} onChange={e => set('roofArea', Number(e.target.value))} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block">Structure Type</Label>
                  <Select value={form.structureType} onValueChange={v => set('structureType', v)}>
                    <SelectTrigger data-testid="structure-type-select"><SelectValue placeholder="Select structure" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low-rise">Low-Rise</SelectItem>
                      <SelectItem value="mid-rise">Mid-Rise</SelectItem>
                      <SelectItem value="high-rise">High-Rise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-xs text-slate-500 flex items-end pb-2">
                  <span>Structure type drives the mounting-system pricing &amp; safety spec.</span>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>System Size</Label>
                  <div className="flex items-center gap-2">
                    <Input data-testid="kw-input" type="number" step="0.1" min="1" max="500" value={form.kw} onChange={e => set('kw', Math.max(1, Math.min(500, Number(e.target.value) || 1)))} className="w-24 h-8 text-right font-bold text-emerald-900" />
                    <span className="font-bold text-emerald-900">kW</span>
                  </div>
                </div>
                <Slider value={[Number(form.kw)]} onValueChange={v => set('kw', v[0])} min={1} max={500} step={0.1} className="[&_[role=slider]]:bg-amber-500 [&_[role=slider]]:border-amber-500" />
                <div className="flex justify-between text-xs text-slate-500 mt-1"><span>1 kW</span><span>500 kW</span></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-2"><Label>Shading Loss</Label><span className="font-bold text-emerald-900">{form.shading}%</span></div>
                  <Slider value={[form.shading]} onValueChange={v => set('shading', v[0])} min={0} max={30} step={1} />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Tilt Angle {form.azimuth && form.azimuth !== 180 && <span className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-full">auto · azimuth {form.azimuth}°</span>}</Label>
                    <span className="font-bold text-emerald-900">{form.tilt}°</span>
                  </div>
                  <Slider value={[form.tilt]} onValueChange={v => set('tilt', v[0])} min={0} max={45} step={1} />
                </div>
              </div>
              <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-sm flex gap-3">
                <Zap className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <strong>Auto-sizing tip:</strong> A {form.kw} kW system needs roughly {form.kw * 80} sq.ft of shadow-free area. Yours is {form.roofArea} sq.ft — {form.roofArea >= form.kw * 80 ? <span className="text-emerald-700 font-semibold">sufficient ✅</span> : <span className="text-rose-700 font-semibold">may need optimization ⚠️</span>}.
                </div>
              </div>
            </div>
          )}

          {/* STEP 4: Components */}
          {step === 4 && (
            <div className="space-y-5" data-testid="step-components">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-emerald-950">Components & BOM</h2>
                <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-100">{stTitle} system</Badge>
              </div>

              {catalogLoading ? (
                <div className="py-12 flex items-center justify-center text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading catalog…
                </div>
              ) : (
              <>
              <div className="flex items-center justify-between p-3 rounded-xl bg-emerald-50 border border-emerald-100">
                <div className="flex items-center gap-2">
                  <BadgeCheck className="w-4 h-4 text-emerald-700" />
                  <div>
                    <div className="text-sm font-semibold text-emerald-950">Show only MNRE-approved components</div>
                    <div className="text-xs text-slate-600">Required for PM Surya Ghar subsidy eligibility.</div>
                  </div>
                </div>
                <Switch checked={mnreOnly} onCheckedChange={setMnreOnly} data-testid="mnre-toggle" />
              </div>

              {/* Phase toggle */}
              <div className="rounded-xl border border-emerald-100 p-3">
                <Label className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2 block">Inverter Phase</Label>
                <div className="flex gap-2">
                  {['single', 'three'].map(ph => (
                    <button
                      key={ph}
                      type="button"
                      onClick={() => set('phase', ph)}
                      data-testid={`phase-${ph}`}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold border-2 transition-all ${
                        form.phase === ph
                          ? 'border-emerald-600 bg-emerald-50 text-emerald-900'
                          : 'border-emerald-100 text-slate-500 hover:border-emerald-300'
                      }`}
                    >
                      {ph === 'single' ? 'Single-Phase' : 'Three-Phase'} <span className="text-[10px] text-slate-400 ml-1">{ph === 'single' ? '(≤6 kW)' : '(>6 kW)'}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Panel */}
              <div>
                <div className="flex items-end justify-between mb-2 gap-3">
                  <Label>Solar Panel <span className="text-xs text-slate-500">({panels.length} available)</span></Label>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-slate-500">Qty</Label>
                    <Input
                      data-testid="panel-qty"
                      type="number" min="0" max="100"
                      value={form.panelQty}
                      onChange={e => set('panelQty', Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                      className="w-20 h-8 text-right"
                      placeholder="auto"
                    />
                    <span className="text-xs text-slate-500">{form.panelQty === 0 ? `(auto: ${autoPanelCount})` : ''}</span>
                  </div>
                </div>
                {panels.length === 0 ? (
                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm text-amber-900">No {mnreOnly ? 'MNRE-approved ' : ''}panels available. Turn off filter or add items in BOM Library.</div>
                ) : (
                  <div className="grid sm:grid-cols-3 gap-3 max-h-[240px] overflow-y-auto pr-1">
                    {panels.map(p => (
                      <button key={p.id} data-testid={`panel-card-${p.id}`} onClick={() => set('panelId', p.id)} type="button" className={`text-left p-3 rounded-xl border-2 transition-all ${form.panelId === p.id ? 'border-emerald-600 bg-emerald-50' : 'border-emerald-100 hover:border-emerald-300'}`}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-bold text-emerald-700">{p.brand}</span>
                          {p.mnre_approved && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full"><BadgeCheck className="w-2.5 h-2.5" />MNRE</span>}
                        </div>
                        <div className="text-sm font-semibold text-emerald-950 mt-1">{p.model}</div>
                        <div className="text-xs text-slate-500 mt-1">{p.wattage}W · {inr(p.price)}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Inverter */}
              <div>
                <Label className="mb-2 block">Inverter <span className="text-xs text-slate-500">({inverters.length} available for {form.phase === 'single' ? 'Single-Phase' : 'Three-Phase'} {stTitle})</span></Label>
                {inverters.length === 0 ? (
                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm text-amber-900">
                    No {mnreOnly ? 'MNRE-approved ' : ''}{form.system_type} inverters found for {form.phase}-phase. Try toggling phase or MNRE filter.
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3 max-h-[240px] overflow-y-auto pr-1">
                    {inverters.map(p => (
                      <button key={p.id} data-testid={`inverter-card-${p.id}`} onClick={() => set('inverterId', p.id)} type="button" className={`text-left p-3 rounded-xl border-2 transition-all ${form.inverterId === p.id ? 'border-emerald-600 bg-emerald-50' : 'border-emerald-100 hover:border-emerald-300'}`}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-bold text-emerald-700">{p.brand}</span>
                          {p.mnre_approved && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full"><BadgeCheck className="w-2.5 h-2.5" />MNRE</span>}
                          {p.phase && <span className="text-[9px] font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-full">{p.phase === 'single' ? '1φ' : '3φ'}</span>}
                        </div>
                        <div className="text-sm font-semibold text-emerald-950 mt-1">{p.model}</div>
                        <div className="text-xs text-slate-500 mt-1">{p.kw} kW · {inr(p.price)}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Battery — only for off-grid & hybrid */}
              {form.system_type !== 'on-grid' && (
                <div>
                  <div className="flex items-end justify-between mb-2 gap-3">
                    <Label className="flex items-center gap-1.5"><Battery className="w-4 h-4 text-emerald-700" /> Battery <span className="text-xs text-slate-500">({batteries.length} available for {stTitle})</span></Label>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500">Qty</Label>
                      <Input
                        data-testid="battery-qty"
                        type="number" min="1" max="100"
                        value={form.batteryQty}
                        onChange={e => set('batteryQty', Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
                        className="w-20 h-8 text-right"
                      />
                    </div>
                  </div>
                  {batteries.length === 0 ? (
                    <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm text-amber-900">No batteries seeded for {form.system_type}. Add them in BOM Library.</div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3 max-h-[240px] overflow-y-auto pr-1">
                      {batteries.map(b => (
                        <button key={b.id} data-testid={`battery-card-${b.id}`} onClick={() => set('batteryId', b.id)} type="button" className={`text-left p-3 rounded-xl border-2 transition-all ${form.batteryId === b.id ? 'border-emerald-600 bg-emerald-50' : 'border-emerald-100 hover:border-emerald-300'}`}>
                          <span className="text-xs font-bold text-emerald-700">{b.brand}</span>
                          <div className="text-sm font-semibold text-emerald-950 mt-1">{b.model}</div>
                          <div className="text-xs text-slate-500 mt-1">
                            {b.kva ? `${b.kva} kVA` : b.ah ? `${b.ah} Ah` : ''} · {inr(b.price)}
                          </div>
                          {b.description && <div className="text-[10px] text-slate-400 mt-1">{b.description}</div>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Mounting Structure */}
              <div>
                <Label className="mb-2 flex items-center gap-1.5"><Wrench className="w-4 h-4 text-emerald-700" /> HOT GALVANIZED STRUCTURE <span className="text-xs text-slate-500">({structures.length} available · pick one)</span></Label>
                <div className="grid sm:grid-cols-2 gap-3 mb-3">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Structure Type</Label>
                    <Select value={form.structureType} onValueChange={v => set('structureType', v)}>
                      <SelectTrigger data-testid="structure-type-select-step4"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low-rise">Low-Rise</SelectItem>
                        <SelectItem value="mid-rise">Mid-Rise</SelectItem>
                        <SelectItem value="high-rise">High-Rise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">kW Quantity</Label>
                    <Select value={String(form.structureQuantity)} onValueChange={v => set('structureQuantity', Number(v))}>
                      <SelectTrigger data-testid="structure-qty-select-step4"><SelectValue placeholder="Qty" /></SelectTrigger>
                      <SelectContent>
                        {Array.from({length: 20}, (_, i) => i + 1).map(n => <SelectItem key={n} value={String(n)}>{n} kW</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {structures.length === 0 ? (
                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm text-amber-900">No structure items in BOM. Add them in BOM Library → Structure tab.</div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3 max-h-[220px] overflow-y-auto pr-1">
                    {structures.map(s => (
                      <button key={s.id} data-testid={`structure-card-${s.id}`} onClick={() => set('structureId', s.id)} type="button" className={`text-left p-3 rounded-xl border-2 transition-all ${form.structureId === s.id ? 'border-emerald-600 bg-emerald-50' : 'border-emerald-100 hover:border-emerald-300'}`}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-xs font-bold text-emerald-700">{s.brand}</span>
                          {s.structure_type && <span className="text-[9px] font-bold bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded-full">{s.structure_type}</span>}
                        </div>
                        <div className="text-sm font-semibold text-emerald-950 mt-1">{s.model}</div>
                        <div className="text-xs text-slate-500 mt-1">{inr(s.price)} · {s.unit || 'per unit'}</div>
                        {s.description && <div className="text-[10px] text-slate-400 mt-1">{s.description}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Cable charges (moved below structure) */}
              <div>
                <Label className="mb-2 flex items-center gap-1.5"><Cable className="w-4 h-4 text-emerald-700" /> Cable Charges</Label>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Number of Floors</Label>
                    <Select value={String(form.floors)} onValueChange={v => set('floors', Number(v))}>
                      <SelectTrigger data-testid="floors-trigger"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n} floor{n > 1 ? 's' : ''}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Cable Charge (from BOM)</Label>
                    <div
                      data-testid="cable-price-display"
                      className="h-10 flex items-center px-3 rounded-md border border-emerald-100 bg-emerald-50 text-emerald-950 font-semibold"
                    >
                      {cable ? inr(cable.price) : '—'}
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">Auto-picked from BOM Library → Cables tab for selected floor count.</div>
                  </div>
                </div>
              </div>

              {/* Accessories (multi-select) */}
              <div>
                <Label className="mb-2 flex items-center gap-1.5"><Shield className="w-4 h-4 text-emerald-700" /> Accessories <span className="text-xs text-slate-500">({accessories.length} available · pick multiple)</span></Label>
                {accessories.length === 0 ? (
                  <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 text-sm text-amber-900">No accessories in BOM. Add them in BOM Library → Accessories tab.</div>
                ) : (
                  <div className="grid sm:grid-cols-2 gap-3 max-h-[260px] overflow-y-auto pr-1">
                    {accessories.map(a => {
                      const active = form.accessoryIds.includes(a.id);
                      return (
                        <div key={a.id} className={`p-3 rounded-xl border-2 transition-all ${active ? 'border-emerald-600 bg-emerald-50' : 'border-emerald-100'}`}>
                          <button
                            type="button"
                            data-testid={`accessory-card-${a.id}`}
                            onClick={() => setForm(f => ({
                              ...f,
                              accessoryIds: active
                                ? f.accessoryIds.filter(id => id !== a.id)
                                : [...f.accessoryIds, a.id],
                              accessoryQty: active
                                ? f.accessoryQty
                                : { ...f.accessoryQty, [a.id]: f.accessoryQty[a.id] || 1 },
                            }))}
                            className="text-left w-full"
                          >
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-xs font-bold text-emerald-700">{a.brand}</span>
                              {active && <span className="text-[9px] font-bold bg-emerald-600 text-white px-1.5 py-0.5 rounded-full">SELECTED</span>}
                            </div>
                            <div className="text-sm font-semibold text-emerald-950 mt-1">{a.model}</div>
                            <div className="text-xs text-slate-500 mt-1">{inr(a.price)} · {a.unit || 'per unit'}</div>
                          </button>
                          {active && (
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-emerald-100">
                              <Label className="text-xs text-slate-500">Qty</Label>
                              <Input
                                data-testid={`accessory-qty-${a.id}`}
                                type="number" min="1" max="100"
                                value={form.accessoryQty[a.id] ?? 1}
                                onChange={e => setForm(f => ({
                                  ...f,
                                  accessoryQty: { ...f.accessoryQty, [a.id]: Math.max(1, Number(e.target.value) || 1) },
                                }))}
                                className="w-20 h-8 text-right"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* BOM Summary */}
              <div className="rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm space-y-1.5" data-testid="bom-summary">
                <div className="flex items-center justify-between"><span className="text-amber-900">Panels ({panel?.wattage || '—'}W)</span><span className="font-bold text-emerald-900">{numPanels} × {inr(panel?.price || 0)}</span></div>
                <div className="flex items-center justify-between"><span className="text-amber-900">Inverter</span><span className="font-bold text-emerald-900">{inr(inverter?.price || 0)}</span></div>
                {battery && (
                  <div className="flex items-center justify-between"><span className="text-amber-900">Battery ({form.batteryQty}×)</span><span className="font-bold text-emerald-900">{inr(batteryCost)}</span></div>
                )}
                {cable && (
                  <div className="flex items-center justify-between"><span className="text-amber-900">Cabling ({form.floors} floor{form.floors > 1 ? 's' : ''})</span><span className="font-bold text-emerald-900">{inr(cableCost)}</span></div>
                )}
                {structure && (
                  <div className="flex items-center justify-between"><span className="text-amber-900">Hot Galvanized Structure ({form.structureQuantity} kW · {form.structureType})</span><span className="font-bold text-emerald-900">{inr(structureCost)}</span></div>
                )}
                {selectedAccessories.length > 0 && (
                  <div className="flex items-center justify-between"><span className="text-amber-900">Accessories ({selectedAccessories.length} item{selectedAccessories.length > 1 ? 's' : ''})</span><span className="font-bold text-emerald-900">{inr(accessoriesCost)}</span></div>
                )}
              </div>
              </>
              )}
            </div>
          )}

          {/* STEP 5: Financials */}
          {step === 5 && (
            <div className="space-y-5" data-testid="step-financials">
              <h2 className="text-xl font-bold text-emerald-950">Financial Summary</h2>

              {/* Subsidy toggle */}
              <div className="p-4 rounded-xl border-2 border-orange-200 bg-orange-50/60">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-bold text-emerald-950">Include PM Surya Ghar Subsidy?</div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      {form.withSubsidy
                        ? 'Government subsidy will be shown and deducted from Net Investment on the PDF.'
                        : 'Non-subsidy proposal — the PDF will hide all subsidy references and use gross cost as net investment.'}
                    </div>
                  </div>
                  <label className="inline-flex items-center cursor-pointer shrink-0" data-testid="subsidy-toggle-label">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={form.withSubsidy}
                      onChange={e => set('withSubsidy', e.target.checked)}
                      data-testid="subsidy-toggle"
                    />
                    <div className="relative w-11 h-6 bg-slate-300 peer-checked:bg-orange-600 rounded-full transition-colors">
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${form.withSubsidy ? 'translate-x-5' : ''}`}></div>
                    </div>
                    <span className="ml-2 text-xs font-bold text-emerald-900">{form.withSubsidy ? 'ON' : 'OFF'}</span>
                  </label>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { l: '1. Total Price (Before GST)', v: inr(result.bomTotal), c: 'text-slate-900', show: true },
                  { l: `2. Add: GST @ ${result.gstPct}%`, v: '+ ' + inr(result.gstAmount), c: 'text-sky-700', show: true },
                  { l: '3. After GST', v: inr(result.afterGst), c: 'text-slate-700', show: true },
                  { l: `4. Less: Discount (${result.discountPct}%)`, v: '– ' + inr(result.discountAmount), c: 'text-amber-600', show: result.discountAmount > 0 },
                  { l: '5. Total Price (After Discount)', v: inr(result.afterDiscount), c: 'text-slate-700', show: result.discountAmount > 0 },
                  { l: `${result.discountAmount > 0 ? '6' : '4'}. Less: Govt. Subsidy`, v: '– ' + inr(result.subsidy), c: 'text-emerald-700', show: form.withSubsidy && result.subsidy > 0 },
                  { l: 'Net Payable by Customer', v: inr(result.totalWithGst), c: 'text-emerald-950 font-extrabold', show: true },
                  { l: 'Yearly Generation', v: result.yearlyUnits.toLocaleString('en-IN') + ' kWh', c: 'text-emerald-900', show: true },
                  { l: 'Yearly Savings', v: inr(result.yearlySavings), c: 'text-amber-600', show: true },
                  { l: '25-Year Savings', v: inr(result.lifetimeSavings), c: 'text-emerald-700 font-bold', show: true },
                  { l: 'Payback Period', v: result.payback + ' years', c: 'text-emerald-900', show: true },
                  { l: 'CO₂ Offset / year', v: result.co2.toLocaleString('en-IN') + ' kg', c: 'text-emerald-900', show: true },
                ].filter(r => r.show).map(r => (
                  <div key={r.l} className="p-4 rounded-xl bg-emerald-50/50 border border-emerald-100">
                    <div className="text-xs text-slate-500">{r.l}</div>
                    <div className={`text-lg mt-1 ${r.c}`}>{r.v}</div>
                  </div>
                ))}
              </div>
              <div>
                <Label className="mb-1.5 block">Sales Executive Notes</Label>
                <Textarea value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any custom terms, EMI tenure, AMC inclusions…" rows={3} />
              </div>
            </div>
          )}

          {/* STEP 6: Generate */}
          {step === 6 && (
            <div className="space-y-5" data-testid="step-generate">
              <div className="text-center">
                <div className="inline-flex w-16 h-16 rounded-full bg-emerald-100 items-center justify-center mb-3"><Sparkles className="w-8 h-8 text-emerald-700" /></div>
                <h2 className="text-2xl font-extrabold text-emerald-950">Ready to Generate Proposal</h2>
                <p className="mt-2 text-slate-600 max-w-md mx-auto">Review your quote summary below, then generate the PDF.</p>
              </div>

              <div className="rounded-2xl border-2 border-emerald-100 shadow-lg bg-gradient-to-br from-emerald-50/50 to-white p-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">System Type</div>
                    <div className="text-lg font-extrabold text-emerald-900 mt-1">{stTitle}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Capacity</div>
                    <div className="text-lg font-extrabold text-emerald-900 mt-1">{form.kw} kW</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Panels</div>
                    <div className="text-lg font-extrabold text-emerald-900 mt-1">{numPanels}</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Net Payable</div>
                    <div className="text-lg font-extrabold text-amber-600 mt-1">{inr(result.totalWithGst)}</div>
                    <div className="text-[9px] text-slate-500">Incl. GST @ {result.gstPct}%</div>
                  </div>
                </div>
              </div>

              {/* Sales Discount picker */}
              <div className="rounded-2xl border-2 border-amber-100 bg-amber-50/60 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-center gap-2">
                      <IndianRupee className="w-4 h-4 text-amber-700" />
                      <Label className="text-sm font-bold text-amber-900">Sales Team Discount</Label>
                    </div>
                    <div className="text-xs text-amber-800 mt-1">Optional negotiation discount, capped at 5%. Applied on Net Investment.</div>
                    {result.discountAmount > 0 && (
                      <div className="text-xs text-emerald-800 mt-2 font-semibold" data-testid="discount-preview">
                        You save {inr(result.discountAmount)} → Final Net {inr(result.netCost)}
                      </div>
                    )}
                  </div>
                  <div className="w-40">
                    <Select value={String(form.discountPct)} onValueChange={v => set('discountPct', Number(v))}>
                      <SelectTrigger data-testid="discount-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">No Discount</SelectItem>
                        <SelectItem value="3">3%</SelectItem>
                        <SelectItem value="4">4%</SelectItem>
                        <SelectItem value="5">5% (max)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="text-center pt-2">
                <Button data-testid="generate-proposal-btn" onClick={finalize} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white px-8 h-12 text-base">
                  {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <FileCheck className="w-5 h-5 mr-2" />}
                  {saving ? 'Generating…' : 'Generate Proposal Now'}
                </Button>
              </div>
            </div>
          )}

          {step < STEPS.length && (
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-emerald-50">
              <Button variant="outline" onClick={prev} disabled={step === 1} data-testid="wizard-back-btn"><ChevronLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={next} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="wizard-next-btn">Continue <ChevronRight className="w-4 h-4 ml-1" /></Button>
            </div>
          )}
        </Card>

        {/* Live summary */}
        <Card className="p-6 border-emerald-100 h-fit sticky top-24 bg-gradient-to-br from-emerald-700 to-emerald-900 text-white">
          <Badge className="bg-amber-400 text-emerald-950 hover:bg-amber-400">Live Preview · {stTitle}</Badge>
          <h3 className="mt-3 text-xl font-bold">{form.name || 'New customer'}</h3>
          <div className="text-sm text-emerald-100/80">{form.segment} • {form.address}</div>

          <div className="mt-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center"><Sun className="w-5 h-5 text-amber-300" /></div>
              <div>
                <div className="text-xs text-emerald-100/70">System Size</div>
                <div className="text-2xl font-extrabold">{form.kw} kW</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center"><IndianRupee className="w-5 h-5 text-amber-300" /></div>
              <div>
                <div className="text-xs text-emerald-100/70">Net Payable (Incl. GST)</div>
                <div className="text-xl font-extrabold">{inr(result.totalWithGst)}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-amber-300" /></div>
              <div>
                <div className="text-xs text-emerald-100/70">Payback</div>
                <div className="text-xl font-extrabold">{result.payback} years</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center"><Leaf className="w-5 h-5 text-amber-300" /></div>
              <div>
                <div className="text-xs text-emerald-100/70">CO₂ offset / year</div>
                <div className="text-xl font-extrabold">{result.co2.toLocaleString('en-IN')} kg</div>
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-white/10 text-xs text-emerald-100/70">
            All numbers update live as you complete the form. PDF will be generated in step {STEPS.length}.
          </div>
        </Card>
      </div>
    </div>
  );
}
